import { useState, useCallback, useMemo } from 'react';
import { Spinner } from '../common/Spinner';
import { ActivityFilters } from './ActivityFilters';
import { EventFeed } from './EventFeed';
import { useActivityStore } from '../../stores/activityStore';
import { usePolling } from '../../hooks/usePolling';
import type { ActivityEvent } from '../../types/activity';

function filterEvents(events: ActivityEvent[], type: string, status: string): ActivityEvent[] {
  let filtered = events;
  if (type !== 'all') {
    filtered = filtered.filter((e) => e.type === type);
  }
  if (status !== 'all') {
    filtered = filtered.filter((e) => e.status === status);
  }
  return filtered;
}

export default function ActivityPage() {
  const [paused, setPaused] = useState(false);

  const events = useActivityStore((s) => s.events);
  const loading = useActivityStore((s) => s.loading);
  const filters = useActivityStore((s) => s.filters);
  const fetchEvents = useActivityStore((s) => s.fetchEvents);
  const fetchNewEvents = useActivityStore((s) => s.fetchNewEvents);
  const setFilters = useActivityStore((s) => s.setFilters);

  // Initial fetch on mount via usePolling (disabled for incremental)
  usePolling(fetchEvents, 30_000, false);

  // Incremental polling every 15 seconds when not paused
  usePolling(fetchNewEvents, 15_000, !paused);

  const handleHoursChange = useCallback(
    (hours: number) => {
      setFilters({ hours });
      fetchEvents();
    },
    [setFilters, fetchEvents]
  );

  const handleTypeChange = useCallback(
    (type: string) => {
      setFilters({ type });
    },
    [setFilters]
  );

  const handleStatusChange = useCallback(
    (status: string) => {
      setFilters({ status });
    },
    [setFilters]
  );

  const filteredEvents = useMemo(
    () => filterEvents(events, filters.type, filters.status),
    [events, filters.type, filters.status]
  );

  const hasActiveFilters = filters.type !== 'all' || filters.status !== 'all';

  if (loading && events.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <ActivityFilters
        hours={filters.hours}
        type={filters.type}
        status={filters.status}
        eventCount={filteredEvents.length}
        paused={paused}
        onHoursChange={handleHoursChange}
        onTypeChange={handleTypeChange}
        onStatusChange={handleStatusChange}
        onTogglePause={() => setPaused((p) => !p)}
      />
      <EventFeed events={filteredEvents} hasActiveFilters={hasActiveFilters} />
    </div>
  );
}
